import { useEffect, useRef, useState } from "react"
import eventBus from "@ciq/shared/hooks/eventBus"
import { EVENT_BUS_EVENTS } from "@ciq/shared/hooks/eventBusEvents"

const CONFIRMATION_MESSAGE = "You have an upload in progress. Are you sure you want to leave?"

const defaultDependencies = {
  addBeforeUnload: (fn) => window.addEventListener("beforeunload", fn),
  removeBeforeUnload: (fn) => window.removeEventListener("beforeunload", fn)
}

/**
 * Hook to guard against browser refresh/close when there's unsaved work.
 *
 * Note: In-app navigation blocking (useBlocker) requires React Router's data router
 * (createBrowserRouter). This app uses legacy BrowserRouter, so only browser
 * refresh/close is blocked.
 *
 * Listens for "force-navigation" event to allow auth redirects (403, idle timeout)
 * to bypass the guard without showing a confirmation dialog.
 *
 * IMPORTANT: When force-navigation fires, the beforeunload listener is removed
 * SYNCHRONOUSLY (not waiting for React's async state update). This prevents a
 * race condition where window.location triggers beforeunload before React re-renders.
 *
 * @param {boolean} shouldBlock - Whether to block navigation
 * @param {Object} [deps] - Optional dependencies for testing
 */
export function useNavigationGuard(shouldBlock, deps = {}) {
  const [forceAllow, setForceAllow] = useState(false)
  const { addBeforeUnload, removeBeforeUnload } = {
    ...defaultDependencies,
    ...deps
  }

  // Store handler ref so we can remove it synchronously when force-navigation fires
  const handlerRef = useRef(null)
  // Store removeBeforeUnload in a ref so the event handler can access it
  const removeBeforeUnloadRef = useRef(removeBeforeUnload)
  removeBeforeUnloadRef.current = removeBeforeUnload

  // Listen for force-navigation event (auth redirects)
  useEffect(() => {
    const handleForceNavigation = () => {
      // SYNCHRONOUSLY remove the listener before React's async state update
      // This prevents the race condition where window.location triggers
      // beforeunload before the state update causes a re-render
      if (handlerRef.current) {
        removeBeforeUnloadRef.current(handlerRef.current)
        handlerRef.current = null
      }
      setForceAllow(true)
    }
    eventBus.on(EVENT_BUS_EVENTS.FORCE_NAVIGATION, handleForceNavigation)
    return () => eventBus.off(EVENT_BUS_EVENTS.FORCE_NAVIGATION, handleForceNavigation)
  }, [])

  useEffect(() => {
    if (!shouldBlock || forceAllow) return

    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = CONFIRMATION_MESSAGE
      return CONFIRMATION_MESSAGE
    }

    handlerRef.current = handleBeforeUnload
    addBeforeUnload(handleBeforeUnload)

    return () => {
      removeBeforeUnload(handleBeforeUnload)
      handlerRef.current = null
    }
  }, [shouldBlock, forceAllow, addBeforeUnload, removeBeforeUnload])
}
